import { useAuth } from '@/lib/auth/auth';
import type { ActivityResponse } from '@/lib/api/apiTypes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const activityKeys = {
  all: ['activity'] as const,
  current: () => [...activityKeys.all, 'current'] as const,
};

export function useActivity() {
  const hasSession = useAuth((state) => Boolean(state.session));
  const request = useAuth((state) => state.request);

  return useQuery({
    queryKey: activityKeys.current(),
    enabled: hasSession,
    queryFn: () => request<ActivityResponse>('/me/activity'),
    staleTime: 60_000,
  });
}

export function useDismissActivity() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityID: string) =>
      request<void>(`/me/activity/${encodeURIComponent(activityID)}`, {
        method: 'DELETE',
      }),
    onMutate: async (activityID) => {
      await queryClient.cancelQueries({ queryKey: activityKeys.current() });

      const previous = queryClient.getQueryData<ActivityResponse>(activityKeys.current());
      queryClient.setQueryData<ActivityResponse>(activityKeys.current(), (current) =>
        current
          ? {
              ...current,
              activity: current.activity.filter((item) => item.id !== activityID),
            }
          : current
      );

      return { previous };
    },
    onError: (_error, _activityID, context) => {
      if (context?.previous) {
        queryClient.setQueryData(activityKeys.current(), context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: activityKeys.all });
    },
  });
}
